import { XIcon } from '@heroicons/react/outline';
import classNames from 'classnames';
import React, {
  FC,
  Fragment,
  KeyboardEventHandler,
  MouseEventHandler,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { functionNoop } from 'src/utils/function/noop';
import { Checkbox } from '../form/checkbox';
import { TextInput } from 'src/common/form/text-input';
import { JourneyMenuPropsInterface } from '../JourneyMenu';
import { Nullable } from 'src/types/nullable.type';
import { Popover } from '../ui/popover';
import { SmallPanel } from '../ui/small-panel';

export interface MultiSelectItem {
  value: string;
  label: string;
}

interface MultiSelectWithSearchPropsInterface<T>
  extends Pick<
    JourneyMenuPropsInterface,
    'placement' | 'openRef' | 'closeRef' | 'containerClassName' | 'onOpen' | 'onClose'
  > {
  name: string;
  items: T[];
  selectedItems?: T[];
  className?: string;
  searchPlaceholder?: string;
  renderOrigin: (onClick: () => void) => ReactElement;
  hideActionOnNotFound?: boolean;
  hasBackButton?: boolean;
  useStrictValueComparison?: boolean;
  inputClassName?: string;
  onSelectedItemClick?: (item: T) => void;
  onItemSelect?: (item: T) => void;
  onItemDeselect?: (item: T) => void;
  onItemAdd?: (item: T) => void;
  onBack?: () => void;
  onSearchInputChange?: (value: string) => void;
  renderFooter?: () => Nullable<ReactElement>;
  renderAction?: () => Nullable<ReactElement>;
  renderHeader?: () => Nullable<ReactElement>;
  renderNotFound?: (searchInput: string) => Nullable<ReactElement>;
  renderListItem?: (item: T, onClick: MouseEventHandler) => ReactElement;
  renderSelectedList?: (callbacks: {
    onItemClick: (item: T) => () => void;
    onRemoveClick: (item: T) => MouseEventHandler;
  }) => Nullable<ReactElement>;
  openAfterMount?: boolean;
}

interface SelectedItemPropsInterface {
  onClick?: () => void;
  onRemoveClick?: MouseEventHandler;
  className?: string;
  removable?: boolean;
}

export const MultiSelectWithSearchSelectedItem: FC<SelectedItemPropsInterface> = ({
  onClick,
  onRemoveClick,
  removable,
  className,
  children,
}) => (
  <div
    className={classNames(
      'flex items-center space-x-1 shrink-0',
      'px-1 py-[3px]',
      'bg-bedrock-white text-bedrock-black text-bedrock-tab',
      'cursor-pointer rounded hover:opacity-80 capitalize',
      removable && 'group',
      className
    )}
    onClick={onClick}
  >
    {removable && <XIcon onClick={onRemoveClick} className='h-3.5 w-3.5 shrink-0 hidden group-hover:block' />}
    {children}
  </div>
);

export const MultiSelectWithSearch = <T extends MultiSelectItem>({
  renderOrigin,
  renderAction = () => null,
  renderListItem,
  renderNotFound = () => null,
  name,
  openRef,
  closeRef,
  placement = 'bottom-end',
  items,
  selectedItems = [],
  containerClassName,
  searchPlaceholder,
  renderHeader = () => null,
  renderSelectedList = () => null,
  renderFooter = () => null,
  className,
  inputClassName,
  hideActionOnNotFound,
  hasBackButton = false,
  useStrictValueComparison = false,
  onBack,
  onOpen,
  onClose,
  onItemSelect,
  onItemAdd,
  onItemDeselect,
  onSelectedItemClick = functionNoop,
  onSearchInputChange,
  openAfterMount = true,
}: MultiSelectWithSearchPropsInterface<T>) => {
  const [searchInput, setSearchInput] = useState('');
  const onOriginClicked = () => {
    openRef.current();
  };
  const searchInputHasFocusRef = useRef(false);

  const onSelectedItemClicked = (item: T) => () => {
    onSelectedItemClick(item);
  };

  const isItemSelectedBefore = useCallback(
    (item: T) => {
      return selectedItems.some((selectedItem) => {
        return (
          String(selectedItem.value).toLowerCase() === String(item.value).toLowerCase() ||
          (!useStrictValueComparison && selectedItem.label.toLowerCase() === item.label.toLowerCase())
        );
      });
    },
    [selectedItems, useStrictValueComparison]
  );

  const onItemClicked = useCallback(
    (item: T): MouseEventHandler<HTMLDivElement> =>
      (event) => {
        const formattedItem = {
          value: `${item.value}`,
          label: item.label,
        };
        if (!isItemSelectedBefore(formattedItem as T)) {
          onItemSelect && onItemSelect(formattedItem as T);
        } else {
          onItemDeselect && onItemDeselect(formattedItem as T);
        }
        event.nativeEvent.stopImmediatePropagation();
      },
    [isItemSelectedBefore, onItemDeselect, onItemSelect]
  );

  const onItemRemoved =
    (item: T): MouseEventHandler =>
    (event) => {
      event.stopPropagation();
      if (onItemDeselect) {
        onItemDeselect(item);
      }
    };

  const onSearchInputChanged: MouseEventHandler<HTMLInputElement> = (event) => {
    const { value } = event.target as HTMLInputElement;
    setSearchInput(value);
    if (onSearchInputChange) {
      onSearchInputChange(value);
    }
  };

  const onSearchInputFocused = () => {
    searchInputHasFocusRef.current = true;
  };

  const onSearchInputBlurred = () => {
    searchInputHasFocusRef.current = false;
  };

  useEffect(() => {
    return () => {
      searchInputHasFocusRef.current = false;
    };
  }, []);

  const isAddingEnabledForInput = useCallback(
    (value: string) =>
      !items.some((item) => item.value.toLowerCase().includes(value) || item.label.toLowerCase().includes(value)),
    [items]
  );

  const addNewItemByInputValue = useCallback(() => {
    if (onItemAdd) {
      onItemAdd({ label: searchInput, value: searchInput } as T);
    }
    setSearchInput('');
  }, [onItemAdd, searchInput]);

  const onInputKeyDowned: KeyboardEventHandler = (event) => {
    event.stopPropagation();
    if (event.key === 'Enter') {
      if (isAddingEnabledForInput(searchInput)) {
        addNewItemByInputValue();
      }
    }
  };

  const onCreateNewTagClicked = () => {
    addNewItemByInputValue();
  };

  const isSearchPerformedWithEmptyResult = searchInputHasFocusRef.current && items.length === 0;
  const showAction = !hideActionOnNotFound && isSearchPerformedWithEmptyResult;

  return (
    <div className={classNames('flex items-center space-x-2 antialiased relative', containerClassName)}>
      <SmallPanel
        openRef={openRef}
        closeRef={closeRef}
        hasBackButton={hasBackButton}
        onBack={onBack}
        onOpen={onOpen}
        onClose={onClose}
        placement={placement}
        openAfterMount={openAfterMount}
        renderOrigin={() => renderOrigin(onOriginClicked)}
        footer={renderFooter()}
        containerClassName={classNames('max-h-[360px] overflow-y-auto', className)}
      >
        {renderHeader()}
        <TextInput
          id={`keyword-search-${name}`}
          onChange={onSearchInputChanged}
          hideOptional
          inputClasses={inputClassName}
          inputProps={{
            type: 'text',
            value: searchInput,
            autoComplete: 'off',
            placeholder: searchPlaceholder,
            onFocus: onSearchInputFocused,
            onBlur: onSearchInputBlurred,
            onKeyDown: onInputKeyDowned,
          }}
        />
        <div className='flex flex-col space-y-sm'>
          {items.map((item) =>
            renderListItem ? (
              <Fragment key={`list-item-${item.value}`}>{renderListItem(item, onItemClicked(item))}</Fragment>
            ) : (
              <div
                key={`${item.value}-option`}
                onMouseDown={onItemClicked(item)}
                className='hover:opacity-80 hover:bg-bedrock-white'
                style={{ wordBreak: 'break-word' }}
              >
                <Checkbox checked={isItemSelectedBefore(item)} isBedrock label={item.label} />
              </div>
            )
          )}
        </div>
        {isSearchPerformedWithEmptyResult && (
          <>{showAction ? <div onClick={onCreateNewTagClicked}>{renderAction()}</div> : renderNotFound(searchInput)}</>
        )}
      </SmallPanel>
      {renderSelectedList({
        onItemClick: (item) => onSelectedItemClicked(item),
        onRemoveClick: (item) => (event) => onItemRemoved(item)(event),
      })}
    </div>
  );
};
